// NATIVE
import * as React from 'react';

// INSTALLED
import {
  Divider,
  Box,
  Card,
  TextField,
  InputAdornment,
  IconButton,
  Typography,
  Button,
  Autocomplete,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  alpha,
} from '@mui/material';
import Grid from '@mui/material/Grid';
import {
  Search as SearchIcon,
  FilterList as FilterListIcon,
  Clear as ClearIcon,
  Person as PersonIcon,
  CalendarToday as CalendarTodayIcon,
  ExpandMore as ExpandMoreIcon,
  InsertChartOutlined as InsertChartOutlinedIcon,
  BarChart as BarChartIcon,
} from '@mui/icons-material';
import { useDispatch, useSelector } from 'react-redux';
import { useLocation, useNavigate } from 'react-router-dom';

// CUSTOM
import { paddingCardsTables } from '../../../commons';
import { hasPemissions, getUrlBase } from '../../../commons/utils/helpers';
import {
  getQualificationsStore,
  getAlertRiskLevelTriggerLogStore,
} from '../../../store/ratings/thunks';
import { getAllUsersPermissionsService } from '../../../services/usersPermissions.servce';
import { RatingsMetricsSummary } from './components/RatingsMetricsSummary';
import { PanelSeguimientoCaso } from './components/PanelSeguimientoCaso';

// NEW COMPONENTS
import { BannerInfoCalificaciones } from './components/BannerInfoCalificaciones';
import { TableRatingsQualifications } from './components/TableRatingsQualifications';
import { BreadCrumbsAndTitle } from '../../../components/generics/BreadCrumbsAndTitle';
import { AlertsInformativos } from '../../../components/generics/AlertsInformativos';
import { alertInformativo } from '../../../commons/texts';
import AutocompleteField from 'components/formFields/autocompleteField';
import { Formik, Form } from 'formik';
import { useResponseHandler } from '../../../hooks/useResponseHandler';

export const GestionCalificaciones = () => {
  const dispatch = useDispatch();
  const { handleResponse, ResponseComponents } = useResponseHandler();

  const {
    users_select_list,
    monthly_stats,
    monthly_metrics,
    nps,
    csat,
    ces,
    alert_risk_level_trigger_log,
    areas_qualifications,
    qualifications_list,
  }: any = useSelector((state: any) => state.ratingsDevices);
  const { google_bussinesses_list_for_select } = useSelector((state: any) => state.ratingsDevices);
  const [users, setUsers] = React.useState<any[]>(users_select_list || []);
  const [areas, setAreas] = React.useState<any[]>(areas_qualifications || []);
  const [filters, setFilters] = React.useState({
    userId: '',
    areaId: '',
    locationId: '',
    fromDate: '',
    toDate: '',
    reportedEvents: '',
    status: localStorage.getItem('showInactivesQualifications') === 'true' ? 0 : 1,
  });

  const [selectedCase, setSelectedCase] = React.useState<any>(null);

  const location = useLocation();
  const navigate = useNavigate();

  React.useEffect(() => {
    if (location.state?.ratingId) {
      const ratingId = location.state.ratingId;

      // Limpiamos la URL primero para que este efecto no se vuelva a disparar
      navigate(location.pathname, { replace: true, state: {}, preventScrollReset: true });

      let rowToOpen: any = null;
      if (qualifications_list && qualifications_list.length > 0) {
        rowToOpen = qualifications_list.find((q: any) => q.id === ratingId);
      }
      if (!rowToOpen) {
        rowToOpen = { id: ratingId };
      }

      // Le damos tiempo al sistema para que haga el fetching de datos de la tabla (aprox 600ms)
      // de forma que cuando despliegue el caso y haga scroll, el DOM ya esté posicionado
      setTimeout(() => {
        handleViewCase(rowToOpen);
      }, 600);
    }
  }, [location.state?.ratingId]);

  React.useEffect(() => {
    if (users_select_list) {
      setUsers(users_select_list);
    }
  }, [users_select_list]);

  React.useEffect(() => {
    if (areas_qualifications) {
      setAreas(areas_qualifications);
    }
  }, [areas_qualifications]);

  React.useEffect(() => {
    getInitialData();
  }, []);

  const getInitialData = async () => {
    // Fetch qualifications
    getDataQualifications();
  };

  const getDataQualifications = async (currentFilters = filters) => {
    const response = await dispatch(
      getQualificationsStore(
        currentFilters.userId,
        currentFilters.fromDate,
        currentFilters.toDate,
        currentFilters.reportedEvents,
        currentFilters.status,
        currentFilters.areaId,
        currentFilters.locationId
      )
    );
    await handleResponse(response, 'Cargar calificaciones', false);
  };

  const handleFilterChange = (field: string, value: any) => {
    setFilters((prev) => ({ ...prev, [field]: value }));
  };

  const applyFilters = () => {
    getDataQualifications();
  };

  const clearFilters = () => {
    const emptyFilters = {
      userId: '',
      areaId: '',
      locationId: '',
      fromDate: '',
      toDate: '',
      reportedEvents: '',
      status: localStorage.getItem('showInactivesQualifications') === 'true' ? 0 : 1,
    };
    setFilters(emptyFilters);
    getDataQualifications(emptyFilters);
  };

  const handleViewCase = (row: any) => {
    setSelectedCase(row);
    dispatch(getAlertRiskLevelTriggerLogStore(row.id));

    // Hacer scroll hasta el panel de seguimiento
    setTimeout(() => {
      const element = document.getElementById('panel-seguimiento-caso');
      if (element) {
        const headerOffset = 90; // Approximate header height
        const elementPosition = element.getBoundingClientRect().top;
        const offsetPosition = elementPosition + window.pageYOffset - headerOffset;

        window.scrollTo({
          top: offsetPosition,
          behavior: 'smooth',
        });
      } else {
        window.scrollTo({
          top: document.body.scrollHeight,
          behavior: 'smooth',
        });
      }
    }, 300);
  };

  return (
    <Box sx={{ p: { xs: 2, md: 3 }, margin: '0 auto' }}>
      {ResponseComponents}

      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Box>
          <BreadCrumbsAndTitle
            title="Gestión de Calificaciones"
            mainModule="calificaciones"
            parent="Gestión"
            icon="StarHalf"
            action="Calificaciones"
          />
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
            <AlertsInformativos
              type={'info'}
              title={'info'}
              content={alertInformativo.gestionCalificaciones}
            />
          </Typography>
        </Box>
      </Box>

      {monthly_stats && monthly_stats.length > 0 && (
        <Accordion
          defaultExpanded
          sx={{
            mb: 3,
            borderRadius: 2,
            boxShadow: '0 2px 10px rgba(0,0,0,0.05)',
            '&:before': { display: 'none' },
            overflow: 'hidden',
          }}
        >
          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
            <Box sx={{ display: 'flex', alignItems: 'center' }}>
              <InsertChartOutlinedIcon sx={{ mr: 1, color: 'primary.main' }} />
              <Typography
                variant="subtitle1"
                sx={{
                  fontWeight: 700,
                  letterSpacing: '0.5px',
                  textTransform: 'uppercase',
                  color: 'text.secondary',
                  fontSize: '0.85rem',
                }}
              >
                Estadísticas Mensuales
              </Typography>
            </Box>
          </AccordionSummary>
          <Divider sx={{ borderStyle: 'dashed' }} />
          <AccordionDetails sx={{ p: 0, bgcolor: alpha('#3f51b5', 0.02) }}>
            <Box
              sx={{
                display: 'grid',
                gridTemplateColumns: {
                  xs: 'repeat(2, 1fr)',
                  sm: 'repeat(4, 1fr)',
                  md: 'repeat(6, 1fr)',
                },
                py: 3,
                px: 2,
                gap: 1.5,
              }}
            >
              {monthly_stats.map((stat: any, index: number) => (
                <Box
                  key={index}
                  sx={{
                    p: 1.5,
                    textAlign: 'center',
                    background: stat.isCurrentMonth ? '#ffffff' : 'transparent',
                    borderTop: stat.isCurrentMonth ? '3px solid #3f51b5' : '3px solid transparent',
                    borderRadius: stat.isCurrentMonth ? '0 0 8px 8px' : 2,
                    boxShadow: stat.isCurrentMonth ? '0 8px 16px rgba(63, 81, 181, 0.08)' : 'none',
                    transition: 'all 0.3s ease',
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    justifyContent: 'center',
                  }}
                >
                  <Typography
                    variant="caption"
                    sx={{
                      fontWeight: stat.isCurrentMonth ? 800 : 600,
                      textTransform: 'uppercase',
                      fontSize: '0.65rem',
                      color: stat.isCurrentMonth ? 'primary.main' : 'text.secondary',
                      letterSpacing: '1px',
                      mb: 1,
                    }}
                  >
                    {stat.month.substring(0, 3)}
                  </Typography>

                  <Box
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      gap: 0.5,
                    }}
                  >
                    <BarChartIcon
                      sx={{
                        fontSize: '1.2rem',
                        color:
                          stat.count === 0
                            ? 'text.disabled'
                            : stat.isCurrentMonth
                              ? 'primary.main'
                              : 'text.disabled',
                        opacity: stat.count === 0 ? 0.4 : stat.isCurrentMonth ? 1 : 0.6,
                      }}
                    />
                    <Typography
                      variant={stat.count === 0 ? 'caption' : 'h5'}
                      sx={{
                        fontWeight: stat.count === 0 ? 600 : 800,
                        color:
                          stat.count === 0
                            ? 'text.disabled'
                            : stat.isCurrentMonth
                              ? 'text.primary'
                              : 'text.secondary',
                        lineHeight: 1,
                        fontStyle: stat.count === 0 ? 'italic' : 'normal',
                        letterSpacing: stat.count === 0 ? '0.5px' : 'normal',
                      }}
                    >
                      {stat.count === 0 ? 'Sin datos' : stat.count}
                    </Typography>
                  </Box>

                  {stat.isCurrentMonth && (
                    <Box
                      sx={{
                        mt: 1.5,
                        width: 6,
                        height: 6,
                        borderRadius: '50%',
                        bgcolor: 'primary.main',
                        boxShadow: '0 0 6px rgba(63, 81, 181, 0.5)',
                      }}
                    />
                  )}
                </Box>
              ))}
            </Box>
          </AccordionDetails>
        </Accordion>
      )}

      <RatingsMetricsSummary nps={nps} csat={csat} ces={ces} monthlyMetrics={monthly_metrics} />

      <Card sx={{ p: { xs: 2, sm: 3 }, borderRadius: 2, boxShadow: 3, mb: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
          <FilterListIcon sx={{ mr: 1, color: 'primary.main' }} />
          <Typography variant="h6" sx={{ fontWeight: 600 }}>
            Filtros
          </Typography>
        </Box>
        <Grid container spacing={2} alignItems="center">
          <Grid size={{ xs: 12, sm: 6, md: 2 }}>
            <Formik
              initialValues={{
                location:
                  google_bussinesses_list_for_select?.find(
                    (loc: any) => loc.value === filters.locationId
                  ) || null,
              }}
              onSubmit={() => {}}
              enableReinitialize
            >
              <Form>
                <AutocompleteField
                  options={google_bussinesses_list_for_select}
                  name="location"
                  label="Negocio / Ubicación"
                  onChange={(event: any, newValue: any) =>
                    handleFilterChange('locationId', newValue?.value || '')
                  }
                />
              </Form>
            </Formik>
          </Grid>
          <Grid size={{ xs: 12, sm: 6, md: 2 }}>
            <Autocomplete
              options={Array.isArray(users) ? users : []}
              getOptionLabel={(option: any) => option.name || option.fullName || ''}
              value={
                Array.isArray(users) ? users.find((u) => u.id === filters.userId) || null : null
              }
              onChange={(_, newValue) => handleFilterChange('userId', newValue?.id || '')}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Agente"
                  size="small"
                  InputProps={{
                    ...params.InputProps,
                    startAdornment: (
                      <InputAdornment position="start">
                        <PersonIcon fontSize="small" />
                      </InputAdornment>
                    ),
                  }}
                />
              )}
            />
          </Grid>
          <Grid size={{ xs: 12, sm: 6, md: 2 }}>
            <Autocomplete
              options={Array.isArray(areas) ? areas : []}
              getOptionLabel={(option: any) => option.label || ''}
              value={
                Array.isArray(areas) ? areas.find((a) => a.value === filters.areaId) || null : null
              }
              onChange={(_, newValue) => handleFilterChange('areaId', newValue?.value || '')}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Área"
                  size="small"
                  InputProps={{
                    ...params.InputProps,
                    startAdornment: (
                      <InputAdornment position="start">
                        <FilterListIcon fontSize="small" />
                      </InputAdornment>
                    ),
                  }}
                />
              )}
            />
          </Grid>
          <Grid size={{ xs: 12, sm: 4, md: 2 }}>
            <TextField
              label="Desde"
              type="date"
              size="small"
              fullWidth
              InputLabelProps={{ shrink: true }}
              value={filters.fromDate}
              onChange={(e) => handleFilterChange('fromDate', e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <CalendarTodayIcon fontSize="small" />
                  </InputAdornment>
                ),
              }}
            />
          </Grid>
          <Grid size={{ xs: 12, sm: 4, md: 2 }}>
            <TextField
              label="Hasta"
              type="date"
              size="small"
              fullWidth
              InputLabelProps={{ shrink: true }}
              value={filters.toDate}
              onChange={(e) => handleFilterChange('toDate', e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <CalendarTodayIcon fontSize="small" />
                  </InputAdornment>
                ),
              }}
            />
          </Grid>
          <Grid size={{ xs: 12, sm: 4, md: 2 }}>
            <TextField
              label="Cant. Eventos"
              type="number"
              size="small"
              fullWidth
              value={filters.reportedEvents}
              onChange={(e) => handleFilterChange('reportedEvents', e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <InsertChartOutlinedIcon fontSize="small" />
                  </InputAdornment>
                ),
              }}
            />
          </Grid>
          <Grid
            size={{ xs: 12, md: 3 }}
            sx={{ display: 'flex', gap: 1, justifyContent: 'flex-end', alignItems: 'center' }}
          >
            <Button
              variant="contained"
              fullWidth
              onClick={applyFilters}
              startIcon={<SearchIcon />}
              sx={{ height: 40 }}
            >
              Buscar
            </Button>
            <IconButton
              onClick={clearFilters}
              color="secondary"
              sx={{ border: '1px solid', borderColor: 'divider', height: 40, width: 40 }}
            >
              <ClearIcon />
            </IconButton>
          </Grid>
        </Grid>
      </Card>

      <TableRatingsQualifications
        filters={filters}
        handleViewCase={handleViewCase}
        onStatusChange={(checked: boolean) => {
          const newStatus = checked ? 0 : 1;
          setFilters((prev) => {
            const newFilters = { ...prev, status: newStatus };
            getDataQualifications(newFilters);
            return newFilters;
          });
        }}
      />

      <BannerInfoCalificaciones />

      {selectedCase && (
        <PanelSeguimientoCaso
          data={selectedCase}
          onClose={() => {
            setSelectedCase(null);
            applyFilters(); // Recargar la tabla para mostrar el nuevo estado
          }}
        />
      )}
    </Box>
  );
};
